import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Map as MapIcon } from "lucide-react";

export default function ScoutTripsDialog({ open, onOpenChange, onOpen, currentPin, currentRadius, onSaved }) {
    const [trips, setTrips] = useState([]);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/scout-trips");
                setTrips(data);
            } catch { /* ignore */ }
        })();
    }, [open]);

    const save = async (e) => {
        e.preventDefault();
        if (!currentPin) { toast.error("Drop an explore pin first"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/scout-trips", {
                name: name.trim() || currentPin.display_name?.split(",")[0] || "Untitled trip",
                lat: currentPin.lat, lng: currentPin.lng,
                radius_km: currentRadius, address: currentPin.display_name || "",
            });
            setTrips((prev) => [data, ...prev]);
            setName("");
            onSaved?.(data);
            toast.success("Trip saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const del = async (trip_id) => {
        try {
            await api.delete(`/scout-trips/${trip_id}`);
            setTrips((p) => p.filter((t) => t.trip_id !== trip_id));
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">Explore trips</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">
                    Bookmark a map pin with its radius so you can re-open it later. Great for planning weekends.
                </p>

                {currentPin && (
                    <form onSubmit={save} className="flex items-end gap-2 p-3 rounded-2xl border border-stone-200 bg-secondary/40">
                        <div className="flex-1">
                            <Label className="text-xs">Save current pin</Label>
                            <Input
                                data-testid="trip-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={currentPin.display_name?.split(",")[0] || "e.g. Weekend in Sausalito"}
                                maxLength={80}
                            />
                            <div className="text-[11px] text-muted-foreground mt-1">{currentRadius} km around {currentPin.display_name?.split(",").slice(0, 2).join(",") || "pinned spot"}</div>
                        </div>
                        <Button type="submit" disabled={busy} data-testid="trip-save" className="rounded-full bg-primary hover:bg-primary/90 hover:text-white">Save</Button>
                    </form>
                )}

                <div className="mt-2">
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Saved trips ({trips.length})</div>
                    {trips.length === 0 ? (
                        <div className="mt-3 py-6 text-center text-sm text-muted-foreground">No saved trips yet.</div>
                    ) : (
                        <div className="mt-3 space-y-2">
                            {trips.map((t) => (
                                <div key={t.trip_id} data-testid={`trip-${t.trip_id}`} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:bg-secondary/40 transition-colors">
                                    <button
                                        onClick={() => { onOpen(t); onOpenChange(false); }}
                                        data-testid={`trip-open-${t.trip_id}`}
                                        className="flex-1 flex items-center gap-2 text-left"
                                    >
                                        <MapIcon size={14} className="text-primary" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-bold truncate">{t.name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{t.radius_km} km · {t.address?.split(",").slice(0, 2).join(",") || `${t.lat.toFixed(3)}, ${t.lng.toFixed(3)}`}</div>
                                        </div>
                                    </button>
                                    <button
                                        data-testid={`trip-delete-${t.trip_id}`}
                                        onClick={() => del(t.trip_id)}
                                        className="text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter />
            </DialogContent>
        </Dialog>
    );
}
